<template>
    <app-layout>
        <base-header :back-url="route('stores.budget-projections.index', [store.id])" :shadow="false" border
                     border-bg-from="from-yellow-500"
                     border-bg-to="to-orange-500"
        >
            <div class="flex items-center justify-between w-full">
                <div v-if="budgetProjection">
                    <h4 class="text-xl font-medium flex space-x-2 items-center">
                        {{ $t('general.budget-projection') }} {{ budgetProjection.year }}
                        <span v-if="budgetProjection && budgetProjection.name">
                            &nbsp;- {{ budgetProjection.name }}
                        </span>

                        <span class="h-full flex items-center">
                            &nbsp;<status-badge :budget="budgetProjection"/>
                        </span>
                    </h4>
                    <div class="flex flex-col">
                        <span v-if="!auth.user.isAdmin || auth.user.stores.length === 1">
                            {{ store.name }}
                        </span>

                        <store-navigation
                            v-else
                            :store="store"
                            @navigate="$inertia.visit(route('stores.budget-projections.index', {store: $event.id, year: budgetProjection.year}))"
                        />
                    </div>
                </div>
                <div v-else class="flex flex-col space-y-1">
                    <element-placeholder height="h-6" width="w-80"/>
                    <element-placeholder width="w-96"/>
                    <element-placeholder width="w-64"/>
                </div>

                <action-bar v-if="budgetProjection && !budgetProjection.is_locked">
                    <default-button
                        :disabled="budgetProjection.is_in_review"
                        :label="budgetProjection.is_locked ? $t('review.already_in_review') : null"
                        :loading="isSendingToReview"
                        icon="mail"
                        @click.native="sendToReview"
                    >
                        {{ $t('review.send_to_review') }}
                    </default-button>
                </action-bar>
            </div>
        </base-header>

        <data-table :actions="actions"
                    :auth="auth"
                    :collection="collection"
                    :delete-modal-title="$t('budget-projection.delete_confirm')"
                    :is-loading="isLoading"
                    :additional-columns="additionalColumns"
                    :fixed-additional-columns-count="additionalColumns ? additionalColumns.columns.length : 0"
        >
            <template v-slot:column-header="slot">
                <template v-if="slot.column.header">
                    <div v-html="slot.column.header"></div>
                </template>

                <template v-else>
                    {{ $moment(slot.column.entity.date).format('MMMM') }}
                </template>
            </template>
        </data-table>

        <div v-if="budgetProjection && budgetProjection.reviews && budgetProjection.reviews.length > 0"
             class="w-full border-t border-gray-300 dark:border-gray-800"
        >
            <base-card :is-default-open="false" flush is-closeable no-padding>
                <template #header>
                    <span class="px-4 py-2">
                        {{ $t('general.reviews') }}
                    </span>
                </template>
                <template #body>
                    <table-base class="text-sm">
                        <template #head>
                            <table-head>{{ $t('general.status') }}</table-head>
                            <table-head>{{ $t('general.reviewer') }}</table-head>
                            <table-head>{{ $t('review.reviewed_at') }}</table-head>
                            <table-head>{{ $t('general.comment') }}</table-head>
                        </template>
                        <template #body>
                            <table-row v-for="review in budgetProjection.reviews" :key="review.id">
                                <table-cell>
                                    <status-badge-review :review="review"/>
                                </table-cell>
                                <table-cell>
                                    {{ review.reviewer ? review.reviewer.name : null }}
                                </table-cell>
                                <table-cell>
                                    <template v-if="review.reviewed_at">
                                        {{ $moment.utc(review.reviewed_at).local().format('D. MMMM Y H:mm:ss') }}
                                    </template>
                                </table-cell>
                                <table-cell>
                                    {{ review.comment }}
                                </table-cell>
                            </table-row>
                        </template>
                    </table-base>
                </template>
            </base-card>
        </div>
    </app-layout>
</template>

<script>
import DataTable from '@/Components/Storemanager/DataTable';
import DataColumn from '@/Components/Storemanager/Entities/DataColumn';
import DataColumnActions from '@/Components/Storemanager/Entities/DataColumnActions';
import DataColumnCollection from '@/Components/Storemanager/Entities/DataColumnCollection';
import Store from '@/Models/Store';
import StoreNavigation from '@/Pages/Stores/Components/StoreNavigation';
import StatusBadge from '@/Pages/Stores/BudgetProjections/Components/StatusBadge';
import StatusBadgeReview from '@/Pages/Reviews/Components/StatusBadge';
import BudgetProjection from '@/Models/BudgetProjection';
import BudgetProjectionEntry from '@/Models/BudgetProjectionEntry';
import Actual from '@/Models/Actual';
import moment from 'moment';
import FieldsModel from '@/Models/Contracts/FieldsModel';

export default {
    components: {StatusBadge, StatusBadgeReview, StoreNavigation, DataTable},

    props: {
        store: Store,
        budget_projection_id: String,
    },

    metaInfo() {
        return {
            title: this.$t('general.budget-projection') +
                (!!this.budgetProjection ? (' - ' + this.budgetProjection.year) : ''),
        };
    },

    data: function() {
        return {
            isDeleting: [],
            collection: new DataColumnCollection(),
            actions: null,
            budgetProjection: null,
            isLoading: true,
            isSendingToReview: false,
            actuals: null,
        };
    },

    created() {
        let budgetProjectionRequest = BudgetProjection.with(['entries', 'reviews']).$find(this.budget_projection_id);

        budgetProjectionRequest.then((response) => {
            this.budgetProjection = response;

            let allMonths = _.range(0, 12).map(num => moment.utc(this.budgetProjection.year+'-01-01').month(num).startOf('month').format('YYYY-MM-DD'));
            let actualsRequest = Actual.where('store_id', this.store.id).whereIn('date', allMonths).$get();

            actualsRequest.then(actualResponse => {
                this.actuals = actualResponse;
                this.isLoading = false;
                this.createCollection();
            });

            this.createActions();
        });
    },

    computed: {
        additionalColumns() {
            if (!this.accumulatedColumn) {
                return null;
            }

            return new DataColumnCollection([this.accumulatedColumn], true);
        },

        accumulatedColumn() {
            if (!this.collection || this.collection.columns.length === 0) {
                return null;
            }

            let entity = new FieldsModel({store: this.store}, false);

            entity.add(this.collection.columns.map(column => column.entity), false);

            const column = new DataColumn(
                entity,
                true,
            );

            column.header = this.$t('general.overall').toString();

            return column;
        },
    },

    methods: {
        sendToReview() {
            this.isSendingToReview = true;

            axios.post(route('api.budget-projections.review', [this.budget_projection_id])).then(response => {
                response.data.budget.entries.map(entry => new BudgetProjectionEntry(entry));
                this.isSendingToReview = false;
                this.isLoading = true;

                BudgetProjection.with(['entries', 'reviews'])
                    .$find(this.budget_projection_id)
                    .then(budgetProjection => {
                        this.budgetProjection = budgetProjection;
                        this.isLoading = false;
                        this.createCollection();
                    });
            });
        },

        createCollection() {
            let collection = [];
            let monthRange = _.range(1, 13);

            for (const monthIndex of monthRange) {
                let column = new DataColumn();

                if (this.actuals) {
                    column.entity = this.actuals.find(
                        actual => moment.utc(actual.date)
                            .isSame(moment.utc(monthIndex + ' ' + this.budgetProjection.year, 'M YYYY'), 'month'),
                    );

                    if (column.entity) {
                        column.header = `
                        <span>${moment(column.entity.date).format('MMMM')} (${this.$t('general.actual')})</span>
                        `;
                    }
                }

                if (!column.entity && this.budgetProjection.entries && this.budgetProjection.entries.length > 0) {
                    column.entity = this.budgetProjection.entries.find(
                        entry => moment.utc(entry.date)
                            .isSame(moment.utc(monthIndex + ' ' + this.budgetProjection.year, 'M YYYY'), 'month'),
                    );
                }

                if (!column.entity) {
                    column.entity = new BudgetProjectionEntry({
                        store: this.store,
                        date: moment.utc(monthIndex + '-' + this.budgetProjection.year, 'M-Y')
                            .startOf('month')
                            .format('YYYY-MM-DD'),
                    }).for(this.budgetProjection);
                }

                collection.push(column);
            }

            this.collection = new DataColumnCollection(
                collection,
                (this.budgetProjection?.is_locked
                    ? this.budgetProjection?.is_locked
                    : this.budgetProjection?.is_in_review)
                ?? false,
            );
        },

        createActions() {
            this.actions = new DataColumnActions();

            this.actions.resetCallback = async (column) => {
                if (column.entity && column.entity.id) {
                    column.isLoading = true;
                    column.entity = await BudgetProjectionEntry.$find(column.entity.id);
                    column.isLoading = false;
                } else {
                    column.entity = new BudgetProjectionEntry({
                        store: this.store,
                        date: moment.utc(column.entity.date).startOf('month').format('YYYY-MM-DD'),
                    }).for(this.budgetProjection);
                }

                column.isEditing = false;
            };

            this.actions.deleteCallback = async (column) => {
                column.isLoading = true;
                await column.entity.delete();

                column.entity = new BudgetProjectionEntry({
                    store: this.store,
                    date: moment.utc(column.entity.date).startOf('month').format('YYYY-MM-DD'),
                }).for(this.budgetProjection);

                column.isLoading = false;
            };
        },
    },
};
</script>
